
import React, { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { AuthLayout } from '@/components/auth/components/AuthLayout';

interface AccountSetupFormProps {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  isLoading: boolean;
  validationError: string;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFirstNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLastNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const AccountSetupForm: React.FC<AccountSetupFormProps> = ({
  email,
  firstName,
  lastName,
  password,
  confirmPassword,
  isLoading,
  validationError,
  onEmailChange,
  onFirstNameChange,
  onLastNameChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit
}) => {
  console.log("Rendering AccountSetupForm with:", { email, firstName, lastName });
  
  // Log when values change
  useEffect(() => {
    console.log("AccountSetupForm props updated:", { email, firstName, lastName });
  }, [email, firstName, lastName, password, confirmPassword]);
  
  return (
    <AuthLayout>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Complete Your Account Setup</CardTitle>
          <CardDescription>
            Create your password to complete the account setup process.
          </CardDescription>
        </CardHeader>
        <form onSubmit={onSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={onEmailChange}
                placeholder="Enter your email address"
                required
                readOnly={!!email} // Make email readonly if it's already set from the invitation
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={onFirstNameChange}
                  placeholder="First name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={onLastNameChange}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={onPasswordChange}
                placeholder="Create a password"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={onConfirmPasswordChange}
                placeholder="Confirm your password"
                required
              />
            </div>
            
            {validationError && (
              <div className="text-red-500 text-sm">{validationError}</div>
            )}
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting Up Account...
                </>
              ) : (
                "Complete Account Setup"
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  );
};

export default AccountSetupForm;
